import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCardModule } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { WebhookStatusComponent } from '@principle-theorem/ng-principle';
import {
  EmptyStateComponent,
  InformationBoxComponent,
  PartnerService,
} from '@principle-theorem/ng-principle-shared';
import {
  BasicDialogService,
  BreadcrumbsStore,
  NgSharedModule,
} from '@principle-theorem/ng-shared';
import { PartnerApp } from '@principle-theorem/principle-core';
import {
  IPartnerApp,
  IWebhook,
  WebhookStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  FieldValue,
  filterUndefined,
  findProp,
  Firestore,
  HISTORY_DATE_FORMAT,
  httpsCallable,
  serialise,
  SerialisedData,
  snapshot,
  snapshotCombineLatest,
  WithRef,
} from '@principle-theorem/shared';
import { Observable } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { environment } from '../../environments/environment';

@Component({
  selector: 'pr-app-webhooks',
  templateUrl: './app-webhooks.component.html',
  styleUrl: './app-webhooks.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    InformationBoxComponent,
    MatCardModule,
    EmptyStateComponent,
    MatListModule,
    MatMenuModule,
    MatIcon,
    NgSharedModule,
    WebhookStatusComponent,
  ],
})
export class AppWebhooksComponent {
  webhooks$: Observable<WithRef<IWebhook>[]>;
  app$: Observable<WithRef<IPartnerApp>>;
  apiDocsUrl = environment.apiDocsUrl;
  readonly dateFormat = HISTORY_DATE_FORMAT;
  enabledWebhookStatus = WebhookStatus.Enabled;

  constructor(
    private _basicDialog: BasicDialogService,
    private _snackBar: MatSnackBar,
    private _route: ActivatedRoute,
    partnerService: PartnerService,
    breadcrumbs: BreadcrumbsStore
  ) {
    this.app$ = this._route.data.pipe(
      findProp<WithRef<IPartnerApp>>('partnerApp'),
      filterUndefined()
    );

    this.webhooks$ = this.app$.pipe(
      switchMap((partner) => PartnerApp.webhooks$(partner))
    );

    snapshotCombineLatest([
      partnerService.partner$.pipe(filterUndefined()),
      this.app$,
    ])
      .pipe(take(1), takeUntilDestroyed())
      .subscribe(([partner, app]) => {
        breadcrumbs.setBreadcrumbs([
          {
            label: partner.name,
            path: ['/'],
          },
          {
            label: 'Apps',
          },
          {
            label: app.name,
            path: ['/apps', app.ref.id],
          },
          { label: 'Webhooks' },
        ]);
      });
  }

  async deleteWebhook(webhook: WithRef<IWebhook>): Promise<void> {
    const confirmed = await this._basicDialog.confirm({
      title: 'Delete Webhook',
      prompt: 'Are you sure you want to delete this webhook?',
    });

    if (!confirmed) {
      return;
    }

    await Firestore.deleteDoc(webhook.ref);
    this._snackBar.open('Webhook deleted');
  }

  async enableWebhook(webhook: WithRef<IWebhook>): Promise<void> {
    await Firestore.patchDoc(webhook.ref, {
      status: WebhookStatus.Enabled,
      consecutiveFailures: 0,
      disabledReason: FieldValue.delete(),
    });
    this._snackBar.open('Webhook enabled');
  }

  async disableWebhook(webhook: WithRef<IWebhook>): Promise<void> {
    await Firestore.patchDoc(webhook.ref, {
      status: WebhookStatus.Disabled,
    });
    this._snackBar.open('Webhook disabled');
  }

  async testWebhook(webhook: WithRef<IWebhook>): Promise<void> {
    await snapshot(
      httpsCallable<
        SerialisedData<{
          webhookRef: DocumentReference<IWebhook>;
        }>
      >('http-partner-testWebhook')(
        serialise({
          webhookRef: webhook.ref,
        })
      )
    );
    this._snackBar.open('Test Webhook sent');
  }
}
